import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { normalizeQuillHtml } from '../utils/richText';

const MAX_PASTED_IMAGE_DIMENSION = 1400;
const MIN_PASTED_IMAGE_DIMENSION = 280;
const MAX_PASTED_IMAGE_BYTES = 350_000;
const IMAGE_QUALITY_START = 0.8;
const IMAGE_QUALITY_MIN = 0.4;
const IMAGE_QUALITY_STEP = 0.08;
const IMAGE_DOWNSCALE_STEP = 0.82;
const MAX_COMPRESSION_ATTEMPTS = 10;

type RichTextEditorProps = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  readOnly?: boolean;
};

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () =>
      reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const loadImageFromFile = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const src = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(src);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(src);
      reject(new Error('Failed to load image'));
    };

    image.src = src;
  });

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number
): Promise<Blob | null> =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () =>
      reject(reader.error || new Error('Failed to read compressed image'));
    reader.readAsDataURL(blob);
  });

const getImageSourcesFromHtml = (html: string): string[] => {
  if (!html || typeof DOMParser === 'undefined') {
    return [];
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const sources = Array.from(doc.querySelectorAll('img'))
    .map((img) => (img.getAttribute('src') || '').trim())
    .filter(Boolean);

  return Array.from(new Set(sources));
};

const htmlContainsOnlyImages = (html: string): boolean => {
  if (!html || typeof DOMParser === 'undefined') {
    return false;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;

  if (!body || body.querySelectorAll('img').length === 0) {
    return false;
  }

  const clone = body.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('img, br').forEach((node) => node.remove());

  const text = (clone.textContent || '').replace(/\u00a0/g, ' ').trim();
  return text.length === 0;
};

const fetchImageAsFile = async (src: string): Promise<File | null> => {
  try {
    const response = await fetch(src);
    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) {
      return null;
    }

    const extension = blob.type.split('/')[1] || 'jpg';
    return new File([blob], `pasted-image.${extension}`, { type: blob.type });
  } catch {
    return null;
  }
};

const compressImageForPaste = async (file: File): Promise<string> => {
  if (!file.type.startsWith('image/')) {
    return readFileAsDataUrl(file);
  }

  // GIF paste keeps animation and generally should not be transformed.
  if (file.type === 'image/gif') {
    return readFileAsDataUrl(file);
  }

  const image = await loadImageFromFile(file);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const largestDimension = Math.max(sourceWidth, sourceHeight);
  const scale =
    largestDimension > MAX_PASTED_IMAGE_DIMENSION
      ? MAX_PASTED_IMAGE_DIMENSION / largestDimension
      : 1;

  const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
  const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const context = canvas.getContext('2d');
  if (!context) {
    return readFileAsDataUrl(file);
  }

  const outputType = 'image/jpeg';
  const drawOnCanvas = (width: number, height: number) => {
    canvas.width = width;
    canvas.height = height;
    context.clearRect(0, 0, width, height);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
  };

  const shrinkDimension = (dimension: number) => {
    if (dimension <= MIN_PASTED_IMAGE_DIMENSION) {
      return dimension;
    }
    return Math.max(
      MIN_PASTED_IMAGE_DIMENSION,
      Math.round(dimension * IMAGE_DOWNSCALE_STEP)
    );
  };

  let width = targetWidth;
  let height = targetHeight;
  let quality = IMAGE_QUALITY_START;
  let lastBlob: Blob | null = null;

  for (let attempt = 0; attempt < MAX_COMPRESSION_ATTEMPTS; attempt += 1) {
    drawOnCanvas(width, height);

    const blob = await canvasToBlob(canvas, outputType, quality);
    if (!blob) {
      break;
    }

    lastBlob = blob;
    if (blob.size <= MAX_PASTED_IMAGE_BYTES) {
      return blobToDataUrl(blob);
    }

    if (quality > IMAGE_QUALITY_MIN) {
      quality = Math.max(IMAGE_QUALITY_MIN, quality - IMAGE_QUALITY_STEP);
      continue;
    }

    const nextWidth = shrinkDimension(width);
    const nextHeight = shrinkDimension(height);
    if (nextWidth === width && nextHeight === height) {
      break;
    }

    width = nextWidth;
    height = nextHeight;
    quality = IMAGE_QUALITY_START;
  }

  if (!lastBlob) {
    return readFileAsDataUrl(file);
  }

  const isOriginalAlreadySmall = file.size <= 200_000;
  if (isOriginalAlreadySmall && lastBlob.size >= file.size) {
    return readFileAsDataUrl(file);
  }

  return blobToDataUrl(lastBlob);
};

const compressImageSourceForPaste = async (src: string): Promise<string> => {
  const source = (src || '').trim();
  if (!source) {
    return source;
  }

  const file = await fetchImageAsFile(source);
  if (!file) {
    return source;
  }

  return compressImageForPaste(file);
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  readOnly,
}) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);
  const lastHtmlRef = useRef<string>(value ?? '');
  const isApplyingRef = useRef(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }

    host.innerHTML = '';
    const editorEl = document.createElement('div');
    host.appendChild(editorEl);

    const q = new Quill(editorEl, {
      theme: 'snow',
      placeholder,
      readOnly: !!readOnly,
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link'],
        ],
      },
    });

    quillRef.current = q;

    const initialValue = normalizeQuillHtml(value || '');
    isApplyingRef.current = true;
    q.clipboard.dangerouslyPasteHTML(initialValue);
    lastHtmlRef.current = initialValue;
    isApplyingRef.current = false;

    const handleTextChange = () => {
      if (isApplyingRef.current) {
        return;
      }
      const html = normalizeQuillHtml(q.getSemanticHTML());
      lastHtmlRef.current = html;
      onChange(html);
    };

    q.on('text-change', handleTextChange);
    let isDisposed = false;

    const handlePaste = async (event: ClipboardEvent) => {
      if (event.defaultPrevented || !q.isEnabled()) {
        return;
      }

      const clipboardData = event.clipboardData;
      if (!clipboardData) {
        return;
      }

      const seenFiles = new Set<string>();
      const imageFiles = Array.from(clipboardData.items)
        .filter(
          (item) => item.kind === 'file' && item.type.startsWith('image/')
        )
        .map((item) => item.getAsFile())
        .filter((file): file is File => Boolean(file))
        .filter((file) => {
          const key = `${file.name}:${file.size}:${file.type}`;
          if (seenFiles.has(key)) {
            return false;
          }
          seenFiles.add(key);
          return true;
        });

      const html = clipboardData.getData('text/html') || '';
      const htmlImageSources =
        imageFiles.length === 0 ? getImageSourcesFromHtml(html) : [];
      const hasImageOnlyHtmlPaste =
        htmlImageSources.length > 0 && htmlContainsOnlyImages(html);

      if (imageFiles.length === 0 && !hasImageOnlyHtmlPaste) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      let insertIndex = q.getSelection(true)?.index ?? q.getLength();

      if (imageFiles.length > 0) {
        for (const imageFile of imageFiles) {
          try {
            const compressedImage = await compressImageForPaste(imageFile);
            if (isDisposed) {
              return;
            }

            q.insertEmbed(insertIndex, 'image', compressedImage, 'user');
            insertIndex += 1;
            q.insertText(insertIndex, '\n', 'user');
            insertIndex += 1;
          } catch (error) {
            console.error('Failed to compress pasted image.', error);
          }
        }
      } else {
        for (const source of htmlImageSources) {
          try {
            const compressedImage = await compressImageSourceForPaste(source);
            if (isDisposed) {
              return;
            }

            q.insertEmbed(insertIndex, 'image', compressedImage, 'user');
            insertIndex += 1;
            q.insertText(insertIndex, '\n', 'user');
            insertIndex += 1;
          } catch (error) {
            console.error('Failed to insert pasted image source.', error);
          }
        }
      }

      q.setSelection(insertIndex, 0, 'silent');
    };

    q.root.addEventListener('paste', handlePaste, true);

    return () => {
      q.off('text-change', handleTextChange);
      isDisposed = true;
      q.root.removeEventListener('paste', handlePaste, true);
      quillRef.current = null;
      host.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const q = quillRef.current;
    if (!q) {
      return;
    }

    const next = normalizeQuillHtml(value || '');
    if (next === lastHtmlRef.current) {
      return;
    }

    const selection = q.getSelection();
    isApplyingRef.current = true;
    q.clipboard.dangerouslyPasteHTML(next);
    lastHtmlRef.current = next;
    isApplyingRef.current = false;

    if (selection) {
      q.setSelection(selection);
    }
  }, [value]);

  useEffect(() => {
    const q = quillRef.current;
    if (!q) {
      return;
    }
    q.enable(!readOnly);
  }, [readOnly]);

  return <div ref={hostRef} />;
};
